/**
 * Nova Header Component
 * 
 * A configurable header for the Nova container that allows configuring engine queries
 * based on the query type (SELECT, AGGREGATE, SQL).
 */

import React from 'react';
import { cn } from '../../../../components/ui';
import { Button, Badge, ScrollArea, Collapsible, CollapsibleContent, CollapsibleTrigger, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../../components/ui';
import { toast } from '../../../../components/ui/toast';
import { 
  Table, 
  BarChart, 
  Code, 
  Sparkles,
  Filter,
  SortAsc,
  Loader2,
  ChevronDown,
  ChevronUp,
  ArrowUpDown
} from 'lucide-react';
import { 
  QueryTypeEnum as QueryType,
  Query,
  SelectQueryOptions,
  AggregateQueryOptions,
  SQLQueryOptions,
  SQLDialect,
  AggregateOperationEnum
} from '../../../../lib/engine';
import { NovaHeaderProps } from './types';
import { ColumnRef } from '../../data-grid/types';
import { SelectQueryConfig } from './select-query-config';
import { AggregateQueryConfig } from './aggregate-query-config';
import { SQLQueryConfig } from './sql-query-config';
import { useNovaStoreContext } from '../../nova/nova-store-context';
import { formatRowCount } from '../../data-grid/cell-formatting';
import { buildDefaultQuery } from '../../nova/query-utils';
import { useUser } from '../../../../hooks/use-user';
import { useSubscription } from '../../../../hooks/use-subscription';

/**
 * Nova Header Component
 */
export function NovaHeader({
  query,
  columns = [],
  originalColumns,
  updateQuery,
  className,
  dataName = 'Data View',
  rowCount = 0,
  onToggleAIChat,
  executeQuery,
  topRightSlot,
  lastExecutedView,
  onUpgradeClick
}: NovaHeaderProps) {
  // Get the store for UI state management
  const { 
    isQueryBuilderExpanded, 
    setQueryBuilderExpanded
  } = useNovaStoreContext();
  
  // State to track if a query is currently executing
  const [isExecuting, setIsExecuting] = React.useState(false);
  
  // Hooks for user and subscription
  const { user } = useUser();
  const { hasSubscription } = useSubscription();
  
  // Get the current query type or default to SELECT
  const queryType = query?.type || QueryType.SELECT;
  
  // Function to handle tab clicks - change query type and expand/collapse panel
  const handleTabClick = (newType: QueryType) => {
    // Debug logging
    console.log('handleTabClick', { 
      queryType, 
      newType, 
      areEqual: queryType === newType,
      queryTypeType: typeof queryType,
      newTypeType: typeof newType
    });
    
    // If clicking the already active tab, toggle the panel
    if (queryType === newType) {
      console.log('Toggling query builder expanded');
      setQueryBuilderExpanded(!isQueryBuilderExpanded);
    } else {
      // Change the query type
      console.log('Changing query type and expanding panel');
      handleQueryTypeChange(newType);
      // Expand the panel when changing query type
      setQueryBuilderExpanded(true);
    }
  };
  
  // Function to handle query type changes
  const handleQueryTypeChange = (newType: QueryType) => {
    if (!query || !updateQuery) return;
    
    // If the query type is already the same, do nothing
    if (query.type === newType) return;
    
    // Create a new default query for the new type
    const defaultQuery = buildDefaultQuery({
      queryType: newType,
      table: { path: dataName },
      availableColumns: originalColumns
    });
    
    // Update the query
    updateQuery(defaultQuery);
  };
  
  // Function to handle selected item click
  const handleSelectedItemClick = () => {
    // Collapse the query builder when a selected item is clicked
    setQueryBuilderExpanded(false);
  };
  
  // Function to handle running the query
  const handleRunQuery = React.useCallback(async () => {
    if (!executeQuery) return;
    
    try {
      setIsExecuting(true);
      await executeQuery();
    } catch (error) {
      console.error('Error executing query:', error);
      toast.error('Error executing query', {
        description: error instanceof Error ? error.message : String(error),
        duration: 5000,
        closeButton: true,
      });
    } finally {
      setIsExecuting(false);
    }
  }, [executeQuery]);
  
  // Add keyboard shortcut for running query
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Ctrl+Enter
      if (e.ctrlKey && e.key === 'Enter') {
        handleRunQuery();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleRunQuery]);
  
  return (
    <div className={cn("flex flex-col", className)}>
      {/* Header with tabs and controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 pl-1">
          {/* Query type tabs */}
          <div className="flex items-center gap-1">
            <Button
              variant={queryType === QueryType.SELECT ? "secondary" : "outline"}
              size="sm"
              className="h-8 px-2.5 flex items-center gap-1"
              onClick={() => handleTabClick(QueryType.SELECT)}
              title={queryType === QueryType.SELECT ? 
                (isQueryBuilderExpanded ? "Collapse Select query builder" : "Expand Select query builder") : 
                "Switch to Select query"}
            >
              <Table className="h-3.5 w-3.5" />
              <span>Select</span>
            </Button>
            
            <Button
              variant={queryType === QueryType.AGGREGATE ? "secondary" : "outline"}
              size="sm"
              className="h-8 px-2.5 flex items-center gap-1"
              onClick={() => handleTabClick(QueryType.AGGREGATE)}
              title={queryType === QueryType.AGGREGATE ? 
                (isQueryBuilderExpanded ? "Collapse Aggregate query builder" : "Expand Aggregate query builder") : 
                "Switch to Aggregate query"}
            >
              <BarChart className="h-3.5 w-3.5" />
              <span>Aggregate</span>
            </Button>
            
            <Button
              variant={queryType === QueryType.SQL ? "secondary" : "outline"}
              size="sm"
              className="h-8 px-2.5 flex items-center gap-1"
              onClick={() => handleTabClick(QueryType.SQL)}
              title={queryType === QueryType.SQL ? 
                (isQueryBuilderExpanded ? "Collapse SQL query builder" : "Expand SQL query builder") : 
                "Switch to SQL query"}
            >
              <Code className="h-3.5 w-3.5" />
              <span>SQL</span>
            </Button>

            {/* Run Query Button */}
            <Button
              variant="default"
              size="sm"
              className="h-8 px-2.5 flex items-center gap-1 ml-1.5"
              onClick={handleRunQuery}
              disabled={isExecuting}
              title="Run the current query (Ctrl+Enter)"
            >
              {isExecuting ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  <span>Running...</span>
                </>
              ) : (
                <>
                  <Sparkles className="h-3.5 w-3.5" />
                  <span>Run Query</span>
                  <span className="ml-1 text-xs opacity-70">(Ctrl+Enter)</span>
                </>
              )}
            </Button>

            {!isQueryBuilderExpanded && (
              <div className="text-xs text-muted-foreground ml-3 hidden md:block">
                Click a query type to expand
              </div>
            )}
          </div>
        </div>
        
        {/* Right side controls */}
        <div className="flex items-center gap-2 pr-1">
          {/* Export Button (if provided) */}
          {topRightSlot && (
            <div className="mr-2">
              {React.isValidElement(topRightSlot) && lastExecutedView
                ? React.cloneElement(topRightSlot as React.ReactElement<any>, { view: lastExecutedView })
                : topRightSlot}
            </div>
          )}
          
          {/* Resize indicator */}
          {isQueryBuilderExpanded && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center mr-1">
                    <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">Drag the handle below to resize the query panel</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          
          {/* Collapse/Expand Button */}
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-7 w-7 p-0"
            title={isQueryBuilderExpanded ? "Collapse query builder" : "Expand query builder"}
            onClick={() => setQueryBuilderExpanded(!isQueryBuilderExpanded)}
          >
            {isQueryBuilderExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>
    </div>
  );
} 